import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { GeoArea } from '@/types/GeoArea';
import { getGeoAreaType } from '@/pages/AuctionsNearMe/helpers/geoAreaToUrlParams';
import { getRelatedGeoAreasSelector } from '@/redux/modules/auctionsNearMe/auctionsNearMe.selectors';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { RelatedGeoArea } from '@/redux/modules/auctionsNearMe/catalogsNearMe.types';
import { TextTile } from '../RelatedSearches/TextTile';
import { useAppSelector } from '@/redux/hooks';
import { useMemo } from 'react';

type Props = {
    geoArea?: GeoArea;
};

export const RelatedGeoAreasNew = ({ geoArea }: Props) => {
    const geoAreaType = geoArea ? getGeoAreaType(geoArea) : null;
    const { formatMessage } = useIntl();
    // Get related geo areas from catalog-management API (otherPlaces field in the response)
    const relatedGeoAreas = useAppSelector(getRelatedGeoAreasSelector);

    // Filter out any duplicate related geo areas
    const uniqueRelatedGeoAreas = useMemo(() => {
        return relatedGeoAreas.filter(
            (relatedArea: RelatedGeoArea, index: number, array: RelatedGeoArea[]) =>
                index ===
                array.findIndex(
                    (item) =>
                        item.city.toLowerCase() === relatedArea.city.toLowerCase() &&
                        item.state.toLowerCase() === relatedArea.state.toLowerCase()
                )
        );
    }, [relatedGeoAreas]);

    // Create tiles for related geo areas
    const relatedGeoAreaTiles = useMemo(() => {
        return uniqueRelatedGeoAreas.map((relatedArea: RelatedGeoArea) => {
            let label: string;
            // Show just city when viewing a specific state or city (predefined geo area)
            if (geoAreaType === 'state' || geoAreaType === 'city') {
                label = relatedArea.city;
            } else {
                // Show city, state when viewing country geo area
                // or when no geoArea context (e.g. default bounds or custom search)
                label = `${relatedArea.city}, ${relatedArea.state}`;
            }
            return (
                <TextTile
                    ariaLabel={formatMessage({ id: 'relatedGeoAreaTileAriaLabel' })}
                    isBold
                    key={relatedArea.city}
                    label={label}
                    url={relatedArea.path}
                />
            );
        });
    }, [uniqueRelatedGeoAreas, formatMessage, geoAreaType]);

    return relatedGeoAreaTiles.length > 0 ? (
        <section className="flex flex-col gap-sm">
            <H2>
                <FormattedMessage id="relatedAreasCarouselTitle" />
            </H2>
            <div className="flex flex-wrap items-stretch gap-xs">{relatedGeoAreaTiles}</div>
        </section>
    ) : null;
};
